import { css } from '@emotion/core';
import { Col, Icon, Input, Row, Select } from 'antd';
import Router from 'next/router';
import React from 'react';
import { useDispatch } from 'react-redux';

import { updateSearchFilters } from '@/src/compliance/redux/complianceReducer';
import Button from '@/src/shared/components/Button';
import Card from '@/src/shared/components/Card';
import Container from '@/src/shared/components/Container';
import Form from '@/src/shared/components/Form';
import Heading from '@/src/shared/components/Heading';
import { bp, colors, form as formStyle } from '@/styles';
import formatURL from '@/utils/formatURL';
import { escapeGTMParam, trackGTMEvent } from '@/utils/utils';

const { Option } = Select;

const styles = {
  searchContainer: css`
    padding-left: 0;
    padding-right: 0;
    @media ${bp.sm} {
      padding-left: 16px;
      padding-right: 16px;
    }
    @media print {
      display: none;
    }
  `,
  cardStyle: css`
    padding: 20px 16px;
    @media ${bp.lg} {
      padding: 24px 32px;
    }
  `,
  buttonContainer: css`
    display: flex;
    justify-content: flex-start;
    @media ${bp.md} {
      justify-content: flex-end;
    }
  `,
  headingSpacer: css`
    margin-bottom: 16px;
  `,
  toolTip: css`
    margin-left: 8px;
    font-size: 1.6rem;
    color: ${colors.secondaryBlue};
  `,
  titleContainer: css`
    display: flex;
    margin-bottom: 12px;
    align-items: center;
    h4 {
      margin-bottom: 0;
    }
  `,
};

const ComplianceSearch = props => {
  const { form, options } = props;
  const dispatch = useDispatch();

  const search = async e => {
    e.preventDefault();
    await form.validateFieldsAndScroll(async (err, values) => {
      if (err) return;
      const { SearchText, States, Categories, Charts } = values;

      const selectedCategories = options.Categories.filter(c =>
        (Categories || []).includes(c.CategoryId)
      ).map(c => escapeGTMParam(c.CategoryDescription));
      const selectedCharts = options.Charts.filter(c => (Charts || []).includes(c.ChartId)).map(c =>
        escapeGTMParam(c.ChartTitle)
      );

      trackGTMEvent({
        event: 'complianceSearch',
        text: escapeGTMParam(SearchText || ''),
        state: States ? States.join(', ') : 'All',
        category: selectedCategories?.length > 0 ? selectedCategories.join(', ') : 'All',
        chart: selectedCharts?.length > 0 ? selectedCharts.join(', ') : 'All',
      });

      const queryObj = {
        searchtext: SearchText || '',
        states: States ? States.join() : '',
        categories: Categories ? Categories.join() : '',
        charts: Charts ? Charts.join() : '',
      };
      const formattedURL = formatURL('/compliance/compliassist', queryObj);

      Router.push(formattedURL, formattedURL);
    });
  };

  const updateSearch = (field, value) => {
    dispatch(updateSearchFilters(field, value));
  };

  const renderInputField = fieldProps => {
    const { type, optionId, optionDescription } = fieldProps;
    if (type === 'select') {
      const { name } = fieldProps;
      return (
        <Select
          allowClear
          size="large"
          optionFilterProp="children"
          // suffixIcon={<Icon type="search" />}
          showArrow
          css={formStyle.inputGray}
          mode="multiple"
          onChange={v => {
            updateSearch(name, v);
          }}
        >
          {fieldProps.options.map(option => (
            <Option key={`${option[optionId]}-opt`} value={option[optionId]}>
              {option[optionDescription]}
            </Option>
          ))}
        </Select>
      );
    }
    return <Input css={styles.input} size="large" suffix={<Icon type="search" />} />;
  };

  const renderLabel = label => {
    return (
      <div css={styles.titleContainer}>
        <Heading type="4" weight="bolder" theme="primaryGray">
          {label}
        </Heading>
      </div>
    );
  };

  return (
    <Container css={styles.searchContainer}>
      <Card css={styles.cardStyle}>
        <Form onSubmit={search}>
          <Row type="flex" gutter={16}>
            <Col xs={24} lg={8}>
              <Heading type="3" weight="bolder" theme="primaryGray" css={styles.headingSpacer}>
                Search Compliance Charts
              </Heading>
            </Col>
          </Row>
          <Row type="flex" gutter={16}>
            <Col xs={24} lg={8}>
              <Form.Item label={renderLabel('Text')}>
                {form.getFieldDecorator('SearchText')(renderInputField({ type: 'input' }))}
              </Form.Item>
            </Col>
          </Row>
          <Row type="flex" gutter={16}>
            <Col xs={24} lg={8}>
              <Form.Item label={renderLabel('State')}>
                {form.getFieldDecorator('States')(
                  renderInputField({
                    type: 'select',
                    name: 'State',
                    options: options.States,
                    optionId: 'StateCode',
                    optionDescription: 'StateDescription',
                  })
                )}
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item label={renderLabel('Category')}>
                {form.getFieldDecorator('Categories')(
                  renderInputField({
                    type: 'select',
                    name: 'Categories',
                    options: options.Categories,
                    optionId: 'CategoryId',
                    optionDescription: 'CategoryDescription',
                  })
                )}
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item label={renderLabel('Chart')}>
                {form.getFieldDecorator('Charts')(
                  renderInputField({
                    type: 'select',
                    name: 'Charts',
                    options: options.Charts,
                    optionId: 'ChartId',
                    optionDescription: 'ChartTitle',
                  })
                )}
              </Form.Item>
            </Col>
          </Row>
          <div css={styles.buttonContainer}>
            <Button css={styles.searchButton} size="small" theme="default" htmlType="submit">
              <Icon type="search" />
              Search
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
};

export default Form.create({ name: 'ComplianceSearchForm' })(ComplianceSearch);
